import { useEffect, useMemo, useState, useRef } from 'react';
import { generateMockAnalysis } from '../utils/mockAnalysis';
import { saveAnalysis, addValueSnapshot } from '../utils/storage';
import { hasCredits, CREDIT_COSTS } from '../utils/credits';
import { isNativeApp } from '../utils/platform';
import { saveAnalysisToSupabase, saveValueSnapshotToSupabase } from '../services/analysisService';
import { reliableWrite } from '../services/writeQueue';
import { aiCall, isAbortError } from '../services/aiProxy';
import {
  ANALYSIS_SYSTEM_PROMPT,
  WEB_SEARCH_TOOL,
  MAX_TOOL_ROUNDS,
  buildFullUserPrompt,
  extractServiceRecords,
  normalizeAnalysis,
  parseValueSnapshot,
} from '../services/analysisUtils';

const DEV = import.meta.env.DEV;

const STAGES = [
  'Reading vehicle data...',
  'Checking maintenance schedules...',
  'Analyzing wear patterns...',
  'Searching automotive marketplaces...',
  'Gathering recent sale prices...',
  'Estimating market value...',
  'Building your report...',
];

const STAGES_WITH_FILES = [
  'Reading vehicle data...',
  'Scanning service records...',
  'Extracting service entries...',
  'Saving extracted records...',
  'Checking maintenance schedules...',
  'Analyzing wear patterns...',
  'Searching automotive marketplaces...',
  'Gathering recent sale prices...',
  'Estimating market value...',
  'Building your report...',
];

export default function Processing({ vehicle, vehicleId, serviceLog, receiptPhotos, userId, onComplete, onAddServiceEntries, onToast }) {
  const hasServiceFiles = receiptPhotos?.length > 0;
  const stages = hasServiceFiles ? STAGES_WITH_FILES : STAGES;
  const [stageIndex, setStageIndex] = useState(0);
  const [error, setError] = useState(null);
  const abortRef = useRef(null);

  // Weighted stage timings: early stages advance quickly, later stages take longer
  // This spreads the animation more evenly across the actual API duration
  const stageTimings = useMemo(() => {
    const totalMs = hasServiceFiles ? 70000 : 40000;
    const n = stages.length;
    const lateCount = 2; // last 2 stages get extra weight
    const earlyCount = n - lateCount;
    const earlyWeight = 1;
    const lateWeight = 3;
    const totalWeight = earlyCount * earlyWeight + lateCount * lateWeight;
    return stages.map((_, i) => {
      const weight = i >= n - lateCount ? lateWeight : earlyWeight;
      return (weight / totalWeight) * totalMs;
    });
  }, [hasServiceFiles, stages.length]);

  useEffect(() => {
    // Chain of timeouts for weighted stage progression
    let currentStage = 0;
    let timer = null;

    function advanceStage() {
      if (currentStage < stages.length - 1) {
        currentStage++;
        setStageIndex(currentStage);
        timer = setTimeout(advanceStage, stageTimings[currentStage]);
      }
    }
    // Start first transition after the duration of stage 0
    timer = setTimeout(advanceStage, stageTimings[0]);

    const controller = new AbortController();
    abortRef.current = controller;

    function saveToSupabase(vid, result) {
      if (!userId) return;
      reliableWrite('analysis', { userId, vehicleId: vid, analysis: result }, () =>
        saveAnalysisToSupabase(userId, vid, result)
      );
      const snapshot = parseValueSnapshot(result);
      if (snapshot.marketValueLow || snapshot.marketValueHigh || snapshot.healthScore) {
        reliableWrite('valueSnapshot', { userId, vehicleId: vid, snapshot }, () =>
          saveValueSnapshotToSupabase(userId, vid, snapshot)
        );
      }
    }

    function snapshotValues(vid, result) {
      try {
        const snapshot = parseValueSnapshot(result);
        if (snapshot.marketValueLow || snapshot.marketValueHigh || snapshot.healthScore) {
          addValueSnapshot(vid, snapshot);
        }
      } catch { /* ignore snapshot errors */ }
    }

    async function analyze() {
      // UI-only credit check (server enforces the real check)
      if (!hasCredits(CREDIT_COSTS.vehicleAnalysis)) {
        onToast?.('No credits remaining. Upgrade your plan for more.');
        // Fall back to mock analysis so the vehicle page isn't empty,
        // but mark it so the user knows it's not AI-generated
        const result = generateMockAnalysis(vehicle);
        result._isMock = true;
        saveAnalysis(vehicleId, result);
        snapshotValues(vehicleId, result);
        saveToSupabase(vehicleId, result);
        onComplete(result);
        return;
      }

      let updatedServiceLog = serviceLog || [];

      try {
        // Phase 1: Extract structured service entries from uploaded files
        if (hasServiceFiles) {
          try {
            if (DEV) console.log('[PocketMechanic] Phase 1: Extracting service records from', receiptPhotos.length, 'files');
            const extractedEntries = await extractServiceRecords(receiptPhotos, vehicle, controller.signal);

            if (extractedEntries.length > 0) {
              if (DEV) console.log('[PocketMechanic] Extracted', extractedEntries.length, 'service entries');
              const entriesToSave = extractedEntries.map((e) => ({
                service: e.service || 'Unknown Service',
                date: e.date || null,
                mileage: e.mileage || null,
                cost: e.cost || null,
                type: e.type || 'Independent',
                shopName: e.shopName || null,
                notes: e.notes || '',
                source: 'extracted_from_upload',
              }));

              onAddServiceEntries?.(vehicleId, entriesToSave);

              updatedServiceLog = [
                ...entriesToSave.map((e, i) => ({ ...e, vehicleId, id: `extracted-${i}` })),
                ...updatedServiceLog,
              ];

              if (DEV) console.log('[PocketMechanic] Phase 1 complete, service log now has', updatedServiceLog.length, 'entries');
            } else {
              if (DEV) console.log('[PocketMechanic] Phase 1: No entries extracted from files');
            }
          } catch (err) {
            if (isAbortError(err)) return;
            console.error('[PocketMechanic] Service record extraction failed, continuing with analysis:', err.message);
          }
        }

        // Phase 2: Full vehicle analysis via server-side proxy (tool loop handled server-side)
        if (DEV) console.log('[PocketMechanic] Phase 2: Running full analysis with', updatedServiceLog.length, 'service log entries');
        const { result: content } = await aiCall({
          action: 'chat_tools',
          creditCost: CREDIT_COSTS.vehicleAnalysis,
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: ANALYSIS_SYSTEM_PROMPT },
            { role: 'user', content: buildFullUserPrompt(vehicle, updatedServiceLog) },
          ],
          tools: [WEB_SEARCH_TOOL],
          temperature: 0.4,
          max_tokens: 2000,
          maxToolRounds: MAX_TOOL_ROUNDS,
        }, { signal: controller.signal, timeout: 120000 });

        const cleaned = content.replace(/^```(?:json)?\s*/, '').replace(/\s*```$/, '');
        const parsed = JSON.parse(cleaned);
        const result = normalizeAnalysis({ ...parsed, ...vehicle });
        if (DEV) console.log('[PocketMechanic] Analysis complete:', { healthScore: result.healthScore, items: result.maintenanceItems.length });
        saveAnalysis(vehicleId, result);
        snapshotValues(vehicleId, result);
        saveToSupabase(vehicleId, result);
        onComplete(result);
      } catch (err) {
        if (isAbortError(err)) return;
        console.error('[PocketMechanic] Analysis failed, using mock data:', err.message);
        setError(err);
        const result = generateMockAnalysis(vehicle);
        saveAnalysis(vehicleId, result);
        snapshotValues(vehicleId, result);
        saveToSupabase(vehicleId, result);
        onComplete(result);
      }
    }

    analyze();

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [vehicle, serviceLog, onComplete]);

  return (
    <div className="screen" style={{ justifyContent: 'center', alignItems: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={spinnerContainerStyle}>
          <div style={spinnerStyle} />
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ position: 'absolute' }}>
            <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
          </svg>
        </div>

        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: 8 }}>Analyzing Your Vehicle</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '1.0625rem', marginBottom: 32 }}>
          {vehicle.year} {vehicle.make} {vehicle.model}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' }}>
          {stages.map((label, i) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 12, opacity: i <= stageIndex ? 1 : 0.3, transition: 'opacity 0.4s' }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: i < stageIndex ? 'var(--success)' : i === stageIndex ? 'var(--accent)' : 'var(--bg-elevated)', transition: 'background 0.3s' }}>
                {i < stageIndex ? (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                ) : i === stageIndex ? (
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'white', animation: 'pulse 1s infinite' }} />
                ) : null}
              </div>
              <span style={{ fontSize: '1rem', color: i <= stageIndex ? 'var(--text-primary)' : 'var(--text-muted)' }}>{label}</span>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}

const spinnerContainerStyle = {
  width: 72, height: 72, margin: '0 auto 24px', position: 'relative',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const spinnerStyle = {
  position: 'absolute', inset: 0, borderRadius: '50%',
  border: '2.5px solid var(--border)', borderTopColor: 'var(--accent)',
  animation: 'spin 1s linear infinite',
};
